import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Billing"` — P11-07, in full, laid out as the design draws it (ADR 0018).
 *
 * WHAT IS ON IT: the account state, the declined and out-of-credit banners, the credit balance,
 * auto-reload, the month so far with its projection, payment methods, spend controls including
 * the hard stop, the recent money rows, the tier, the minutes-a-day summary, spend per
 * VoiceAgent and the invoices.
 *
 * FOUR THINGS THIS SCREEN IS CAREFUL ABOUT.
 *
 * **1. Which balance is shown.** Design §7.9 keeps three figures apart — settled, today's
 * unpriced seconds, and what conversations in flight may still cost. The headline is
 * `available`, the one the hard stop evaluates, because a screen that showed `settled` would
 * tell a customer they had credit while their agents had already stopped answering.
 *
 * **2. The declined banner is a second read.** `creditStateOf` knows about money and not about
 * cards, so "declined" cannot come from the balance. It comes from the most recent
 * `charge_attempt`, and a funded account can be declined without being out of credit.
 *
 * **3. Nothing here is a float.** Every amount is `Money` — integer minor units plus a
 * currency — from the wire to `formatMoney`. The prices are EUR, re-denominated rather than
 * converted from the prototype's dollar figures.
 *
 * **4. The design's "Account state" control is a read-out here.** In the prototype it switches
 * the screen between its four worked states; on a real account the state is a fact, so the same
 * four segments are drawn with the account's own state selected and none of them clickable.
 * Receipts, invoice PDFs and quota requests are not built yet, so they keep the design's place
 * and are shown disabled rather than as links that go nowhere.
 *
 * **5. Whose money it is (plan-014 D6; FR12-DOC-05).** The funding line at the top names the balance that pays for
 * the workspace and since when (`GET /v1/billing/funding`). Someone with the organization's finance over that balance
 * (Owner, Admin, Finance — or a dedicated balance's delegate) sees the balance, its cards, automatic top-up, money
 * rows and invoices, and may ask for a refund of unused purchased credit; anyone else — a workspace Billing role
 * included — sees the workspace's SPEND only (this month, the spend cap, minutes, spend per agent), and the API
 * refuses them the rest as well.
 *
 * **6. Which rate (plan-017 WAPP-A; design §29.11 rows 2–5; owner answers OQ-1, OD-C, Q10, OD-B, PQ-2, PQ-3).** No
 * published rate any more. "Minutes of credit" and the low line divide by the organization's HIGHEST rate in force and
 * name it; this month's line is the money actually charged, never seconds × a rate; the rates card lists the
 * organization's own rates and every upcoming change with its start; and the extra-time card shows the switch only
 * where the terms in force include minutes (pay-as-you-go reads one sentence), flipped by the Owner, Admin or Finance
 * with the revision read (`If-Match`; a `409 revision_stale` reloads it and says so).
 *
 * **7. The plan (plan-020 WAPP-BILL; design §32.17, Q-23…Q-28).** A finance reader sees the organization's plan here —
 * `PlanSection` — and, at `?pay={operation}` (the link every plan mail carries), the pay page above everything else;
 * `?card=replace` (the shell banner's "Replace card") opens the card replacement. Removing the card a running plan
 * charges is refused (`409 payment_method_in_use_by_subscription`) and offers the replacement instead.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Field,
  Icon,
  Input,
  Segmented,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { CURRENCY_CODE, money } from "@wetalk/domain";
import { useState, type CSSProperties, type ReactNode } from "react";

import { AddCardDialog, AddCreditDialog } from "./add-credit-dialog.js";
import { ledgerGlyphOf } from "./ledger-glyph.js";
import { SwitchControl } from "./switch-control.js";
import { PendingQuotaRequestLine, QuotaRequestDialog } from "./quota-request-dialog.js";
import { useWorkspaceFunding } from "./funding-line.js";
import type { WorkspaceFundingView } from "../../api/slice/financial-document.js";
import { FinanceKicker, FinanceTabs } from "../finance/finance-tabs.js";
import { RefundDialog } from "./refund-dialog.js";
import { CardReplacementDialog } from "./card-replacement-dialog.js";
import { PlanPayPanel } from "./plan-pay-panel.js";
import { PlanSection } from "./plan-section.js";
import { useSharedSubscription, type BillingSearch } from "./plan-billing.js";
import { refusalCodeOf } from "../finance/plans/plan-copy.js";
import { apiProblemText } from "../../api/api-error.js";
import {
  billedOnLabel,
  portalAvailableTextOf,
  portalCreditPartsOf,
  cardBrandLabel,
  cardExpiryLabel,
  dayKeyOf,
  firstOfMonth,
  formatSignedMoney,
  groupedCount,
  ledgerLabelOf,
  ledgerMethodOf,
  monthLabel,
  noMoney,
  projectedMonthSpend,
  shortDayLabel,
  sumMoney,
  usageRangeOf,
  type UsageRange,
  usageRangeLabel,
} from "./billing-copy.js";
import { GLYPH } from "../../shell/glyph.js";
import { usePopover } from "../../shell/popover.js";
import { useCurrentWorkspace } from "../../shell/current-workspace.js";
import { useGateway } from "../../api/provider.js";
import {
  QUERY_KEY,
  authContextQuery,
  authSessionQuery,
  autoReloadQuery,
  chargeAttemptQuery,
  creditBalanceQuery,
  dashboardQuery,
  invoiceQuery,
  ledgerQuery,
  paymentMethodQuery,
  spendControlQuery,
  usageDayQuery,
  voiceAgentListQuery,
} from "../../api/query.js";
import { meterPercent, meterStyle } from "../../meter.js";
import { formatMoney, formatWholeMoney } from "../../money.js";
import { talkTimeText } from "../../talk-time.js";
import { TalkRatePanel } from "./talk-rate-panel.js";
import { ExtraTimeCard } from "./extra-time-card.js";
import { talkMinuteAffordableOf, talkRateText } from "../../api/slice/talk-rate.js";
import { roleHolds, useOpenAccount } from "../account-admin/overview/account-overview-screen.js";
import type {
  CreditBalanceView,
  LedgerEntryView,
  PaymentMethodView,
  UsageDayView,
} from "../../api/gateway.js";
import { creditFiguresOf } from "../../api/gateway.js";

import "./billing.css";

/** The Minutes-a-day bars are worked in seconds; their ceiling rounds up to a whole hour. */
const SECOND_PER_MINUTE = 60;
const SECOND_PER_HOUR = 3_600;
/** The thresholds and top-up amounts the design offers, in whole euro of minor units. */
const THRESHOLD_MINOR = [5_000, 12_500, 25_000, 50_000] as const;
const TOP_UP_MINOR = [25_000, 50_000, 100_000, 250_000] as const;
const ALERT_PERCENT = [50, 75, 100] as const;

/** The four account states the design names, in its order. */
const ACCOUNT_STATE = ["Healthy", "Low", "Out of credit", "Card declined"] as const;
type AccountState = (typeof ACCOUNT_STATE)[number];

/** Each state's chip colour (review #58): one read-only chip, never a control. */
const ACCOUNT_STATE_STATUS: Readonly<Record<AccountState, "positive" | "caution" | "critical">> = {
  Healthy: "positive",
  Low: "caution",
  "Out of credit": "critical",
  "Card declined": "critical",
};

/** The chip's second half: what the state means for talk time, in a few words. */
export function accountStateDetail(state: AccountState, balance: CreditBalanceView): string {
  if (state === "Out of credit") {
    // 2026-10-04: the credit is held by a conversation in progress, not gone.
    return balance.refusal?.cause === "held_by_open_conversation"
      ? portalText("finance", "m0150")
      : portalText("finance", "m0151");
  }
  if (state === "Card declined") return portalText("finance", "m0152");
  const freeMinute = Math.round(balance.trialSecondsRemaining / 60);
  if (freeMinute > 0) return portalText("finance", "m0153", { value1: String(freeMinute) });
  // OD-C: minutes at the organization's highest rate in force (`talkRatePerMinute` is the API's highest).
  const minute = talkMinuteAffordableOf(balance.available, balance.talkRatePerMinute);
  if (minute === null) return portalText("finance", "billing.rates_free");
  return portalText("finance", "billing.state_minutes_at_highest", { count: minute });
}

/** The design's usage ranges, in its order. */
const USAGE_RANGE: readonly UsageRange[] = ["7 days", "This month", "Last month", "90 days"];

/**
 * The lede under the workspace's name (design sync 2026-10-09): "This workspace's balance, usage and payment method.
 * It draws on the shared balance." — or on its own balance; the second sentence is left out while the funding is
 * unknown, and someone who sees the spend only (no finance access) is told whose money it is instead.
 */
export function billingLedeOf(funding: WorkspaceFundingView | undefined): string {
  if (funding !== undefined && funding.financeAccess === "none") {
    // A workspace Billing role sees the spend only: the lede says so rather than promise a balance it cannot show.
    return portalText("finance", "m0155");
  }
  const first = portalText("finance", "m0156");
  if (funding === undefined) return first;
  return portalText("finance", "m0157", { value1: first, value2: funding.scope === "account" ? portalText("finance", "m1195") : portalText("finance", "m1196") });
}

export interface BillingScreenProps {
  /** `?pay=` and `?card=` (plan-020 WAPP-BILL); absent where a suite mounts the screen on its own. */
  readonly search?: BillingSearch;
  readonly onSearch?: (next: BillingSearch) => void;
}

export function BillingScreen(props: BillingScreenProps = {}): ReactNode {
  usePortalTranslation("finance");
  usePortalTranslation("billing");
  const gateway = useGateway();
  const search = props.search ?? {};
  const setSearch = (next: BillingSearch): void => {
    props.onSearch?.(next);
  };
  // plan-020 WAPP-BILL: the organization's plan, for the Plan section's card replacement.
  const plan = useSharedSubscription();
  const [cardReplacementOpen, setCardReplacementOpen] = useState(false);
  const replacingCard = cardReplacementOpen || search.card === "replace";
  const openCardReplacement = (): void => {
    setCardReplacementOpen(true);
  };
  const closeCardReplacement = (): void => {
    setCardReplacementOpen(false);
    if (search.card !== undefined) setSearch({ ...(search.pay === undefined ? {} : { pay: search.pay }) });
  };
  const at = new Date();
  const fromDay = dayKeyOf(firstOfMonth(at));
  const toDay = dayKeyOf(at);
  const [usageRange, setUsageRange] = useState<UsageRange>("This month");
  const range = usageRangeOf(usageRange, at);

  // plan-014 D6: whose money it is decides what is shown, and what is asked for — never a refused read.
  const { funding, failed: fundingFailed } = useWorkspaceFunding();
  const seesMoney = funding !== undefined && funding.financeAccess !== "none";
  const managesMoney = funding !== undefined && funding.financeAccess === "manage";
  // The workspace's name is the listing's, read on its own (customer-portal review #150).
  const currentWorkspace = useCurrentWorkspace();
  const credit = useQuery(creditBalanceQuery(gateway));
  const attempt = useQuery({ ...chargeAttemptQuery(gateway), enabled: seesMoney });
  const method = useQuery({ ...paymentMethodQuery(gateway), enabled: seesMoney });
  const spend = useQuery(spendControlQuery(gateway));
  const month = useQuery(usageDayQuery(gateway, fromDay, toDay));
  const usage = useQuery(usageDayQuery(gateway, range.fromDay, range.toDay));
  const ledger = useQuery({ ...ledgerQuery(gateway, null), enabled: seesMoney });
  const invoice = useQuery({ ...invoiceQuery(gateway), enabled: seesMoney });
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const dashboard = useQuery(dashboardQuery(gateway));
  // plan-017 WAPP-A (PQ-2): extra time is the organization's — read by its finance readers, flipped by its managers.
  const openAccount = useOpenAccount();
  const readsOrganizationFinance = roleHolds(openAccount.accountRole, "account.finance.read");

  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [quotaRequestOpen, setQuotaRequestOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  if (credit.data === undefined || (funding === undefined && !fundingFailed)) {
    return <p className="c-note">{portalText("finance", "m0158")}</p>;
  }

  const balance = credit.data;
  const latestAttempt = attempt.data?.[0];
  const declined =
    latestAttempt?.state === "declined" || latestAttempt?.state === "requires_reauth";
  const stopped = balance.state === "exhausted" || balance.state === "hard_stopped";
  // 2026-10-04: refused only because conversations in progress hold the credit — it frees up when they end.
  const heldByConversation = balance.refusal?.cause === "held_by_open_conversation";
  // Owner answer 53 (2026-10-07): the configured line (60 minutes of talk at launch), the same one the shell's
  // banner and the low-balance email use.
  const low = !stopped && balance.available.amountMinor < balance.talkRatePerMinute.amountMinor * balance.lowBalanceTalkMinute;
  const accountState: AccountState = declined ? "Card declined" : stopped ? "Out of credit" : low ? "Low" : "Healthy";

  const monthRow = month.data ?? [];
  // Review #109: what billing CHARGES — the free trial covers the seconds first — not the list
  // price of the talk. Review #103: the rows include today's not-yet-closed seconds.
  const spentThisMonth = sumMoney(monthRow.map((one) => one.charged));
  const includesToday = monthRow.some((one) => !one.closed);
  const talkSecondThisMonth = monthRow.reduce((total, one) => total + one.billedSeconds, 0);
  const projected = projectedMonthSpend(spentThisMonth, at);
  const budget = spend.data?.monthBudget ?? null;
  const tier = dashboard.data?.tier;
  // plan-014 GAP (INT decision 2026-10-07): the ORGANIZATION's concurrency entitlement (a granted raise or a plan
  // included), never the tier's own figure — the number admission enforces.
  const organizationConcurrency = dashboard.data?.organizationConcurrency;

  return (
    <section className="c-page bl-page">
      {/* Design sync 2026-10-09: Billing is Finance → "This workspace" — the payer kicker and the Finance tab strip, the
          workspace's name as the title and which balance it draws on as the lede (the funding banner is gone). */}
      <FinanceKicker className="bl-finance-kicker" />
      <FinanceTabs current="billing" className="bl-finance-tabs" />
      {funding === undefined ? (
        <p className="c-note" role="status">{portalText("finance", "m0163")}</p>
      ) : null}
      <p className="bl-kicker">
        {monthLabel(at)}{portalText("finance", "m0164")}{billedOnLabel(at)}
      </p>
      <h1 className="bl-title">{currentWorkspace.workspace?.name ?? portalText("finance", "m0165")}</h1>
      <p className="bl-lede">{billingLedeOf(funding)}</p>

      {/* Review #58: one read-only status chip, not a segmented control that looks clickable. */}
      {/* plan-020 WAPP-BILL: the pay page (`?pay=`), above everything else — every plan mail links here. */}
      {search.pay === undefined ? null : (
        <PlanPayPanel
          operationId={search.pay}
          onClose={() => {
            setSearch({ ...(search.card === undefined ? {} : { card: search.card }) });
          }}
        />
      )}

      <div className="bl-state">
        <span className="bl-state-label">{portalText("finance", "m0166")}</span>
        <Tag status={ACCOUNT_STATE_STATUS[accountState]}>
          {{ "Healthy": portalText("finance", "m0162"), "Low": portalText("finance", "m0161"), "Out of credit": portalText("finance", "m0160"), "Card declined": portalText("finance", "m0159") }[accountState]} — {accountStateDetail(accountState, balance)}
        </Tag>
      </div>

      {declined && latestAttempt !== undefined ? (
        <div className="bl-alert" role="status">
          <Icon glyph={GLYPH.creditCard} size="2xl" />
          <span className="bl-alert-body">
            <span className="bl-alert-title">{portalText("finance", "m0167")}{formatMoney(latestAttempt.amount)}{portalText("finance", "m0168")}{latestAttempt.whenText}
            </span>
            <span className="bl-alert-dek">
              {portalText("finance", "m0169", { value1: latestAttempt.declineMessage ?? portalText("finance", "m1138") })}
            </span>
            {managesMoney ? (
              <span className="bl-alert-actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    setAddCardOpen(true);
                  }}
                >{portalText("finance", "m0170")}</Button>
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      {stopped && heldByConversation ? (
        // 2026-10-04: nothing has stopped for good — a conversation in progress holds what is left.
        <div className="bl-alert" role="status">
          <span className="bl-alert-body">
            <span className="bl-alert-title">{portalText("finance", "m0171")}</span>
            <span className="bl-alert-dek">{balance.refusal?.message}</span>
            {managesMoney ? (
              <span className="bl-alert-actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    setAddCreditOpen(true);
                  }}
                >{portalText("finance", "m0172")}</Button>
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      {stopped && !heldByConversation ? (
        <div className="bl-alert" role="status">
          <span className="bl-alert-body">
            <span className="bl-alert-title">
              {balance.state === "hard_stopped"
                ? portalText("finance", "m0173")
                : portalText("finance", "m0174")}
            </span>
            <span className="bl-alert-points">
              <span className="bl-alert-point">
                <Icon glyph={GLYPH.phoneX} size="lg" />{portalText("finance", "m0175")}</span>
              <span className="bl-alert-point">
                <Icon glyph={GLYPH.pause} size="lg" />{portalText("finance", "m0176")}</span>
              <span className="bl-alert-point">
                <Icon glyph={GLYPH.archive} size="lg" />{portalText("finance", "m0177")}{balance.state === "hard_stopped"
                  ? portalText("finance", "m0178")
                  : ""}
              </span>
            </span>
            {managesMoney ? (
              <span className="bl-alert-actions">
                <Button
                  variant="primary"
                  onClick={() => {
                    setAddCreditOpen(true);
                  }}
                >{portalText("finance", "m0179")}</Button>
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      <div className="bl-cards">
        {/* --- credit balance ------------------------------------------------------- */}
        {seesMoney ? (
        <div className="bl-card">
          {/* 2026-10-04: Available, with its parts — the labels and figures the operator console prints. */}
          <p className="bl-card-kicker">{portalText("finance", "credit.label.available")}</p>
          <p className="bl-figure-row">
            <span className="bl-figure">{portalAvailableTextOf(creditFiguresOf(balance))}</span>
            <Tag tone={accountState === "Healthy" ? "accent" : "neutral"} {...(accountState === "Healthy" ? { status: "positive" as const } : {})}>
              {balanceStateLabel(balance, low)}
            </Tag>
          </p>
          <CreditPartList balance={balance} />
          <p className="bl-card-line">{balanceLine(balance)}</p>
          <div className="bl-actions">
            {managesMoney ? (
              <Button
                variant="primary"
                onClick={() => {
                  setAddCreditOpen(true);
                }}
              >
                <Icon glyph={GLYPH.plus} size="md" />{portalText("finance", "m0172")}</Button>
            ) : null}
            <Link to="/billing/ledger" className="btn btn-secondary">{portalText("finance", "m0180")}</Link>
            {managesMoney ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setRefundOpen(true);
                }}
              >{portalText("finance", "m0181")}</Button>
            ) : null}
          </div>
        </div>
        ) : null}

        {/* --- auto-reload ----------------------------------------------------------- */}
        {seesMoney ? <AutoReloadCard /> : null}

        {/* --- this month ------------------------------------------------------------ */}
        <div className="bl-card">
          <p className="bl-card-kicker">{portalText("finance", "m0182")}</p>
          <p className="bl-figure bl-figure-block">{formatMoney(spentThisMonth)}</p>
          <p className="bl-card-line">
            {/* plan-017 WAPP-A (§29.11 row 3): the talk time and the money charged, no rate multiplied in. */}
            {portalText("finance", "billing.this_month_line", {
              value0: talkTimeText(talkSecondThisMonth),
              value1: formatWholeMoney(money(Math.round(projected.amountMinor / 100) * 100, projected.currency)),
            })}</p>
          {includesToday ? (
            <p className="bl-card-note">{portalText("finance", "m0185")}</p>
          ) : null}
          {budget === null ? (
            <p className="bl-card-note">{portalText("finance", "m0186")}{currentWorkspace.workspace?.name ?? portalText("finance", "m0187")}{portalText("finance", "m0188")}</p>
          ) : (
            <>
              <div className="bl-meter">
                <div
                  className="bl-meter-fill"
                  style={meterStyle(meterPercent(spentThisMonth.amountMinor, budget.amountMinor))}
                />
              </div>
              {balance.workspaceBudget?.reached === true ? (
                // The design's budget line, in the state it does not draw: THIS workspace's hard
                // stop at budget has fired (budget_reached). Spent is admission's own figure.
                <p className="bl-card-note">{portalText("finance", "m0189")}{formatMoney(balance.workspaceBudget.spent)}{portalText("finance", "m0190")}{" "}
                  {currentWorkspace.workspace?.name ?? portalText("finance", "m0187")}{portalText("finance", "m0191")}{formatWholeMoney(budget)}{portalText("finance", "m0192")}</p>
              ) : (
                <p className="bl-card-note">
                  {String(Math.round((spentThisMonth.amountMinor / budget.amountMinor) * 100))}{portalText("finance", "m0193")}{" "}
                  {currentWorkspace.workspace?.name ?? portalText("finance", "m0187")}{portalText("finance", "m0191")}{formatWholeMoney(budget)}{portalText("finance", "m0194")}</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* --- the organization's plan (plan-020 WAPP-BILL, Q-23) ----------------------------- */}
      <PlanSection
        onReplaceCard={openCardReplacement}
        onPayPage={(operationId) => {
          setSearch({ pay: operationId });
        }}
      />

      {/* --- the organization's talk rates and extra time (plan-017 WAPP-A) ------------------ */}
      <div className="bl-pair">
        <div className="bl-card">
          <p className="bl-card-kicker">{portalText("finance", "billing.rates.kicker")}</p>
          <TalkRatePanel variant="billing" />
        </div>
        {readsOrganizationFinance ? <ExtraTimeCard /> : null}
      </div>

      <div className="bl-pair">
        {/* --- payment methods -------------------------------------------------------- */}
        {seesMoney ? (
        <div className="bl-card">
          <div className="bl-card-head">
            <p className="bl-card-title">{portalText("finance", "m0195")}</p>
            {managesMoney ? (
              <span className="bl-flush">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setAddCardOpen(true);
                  }}
                >{portalText("finance", "m0196")}</Button>
              </span>
            ) : null}
          </div>
          {method.data === undefined || method.data.length === 0 ? (
            <p className="bl-card-note">{portalText("finance", "m0197")}</p>
          ) : (
            <div className="bl-card-list">
              {method.data.map((card) => (
                <PaymentMethodRow key={card.paymentMethodId} card={card} onReplaceCard={plan.canManage && plan.held !== null && plan.held !== undefined ? openCardReplacement : null} />
              ))}
            </div>
          )}
          <InvoiceEmailLine />
        </div>
        ) : null}

        {/* --- spend controls ---------------------------------------------------------- */}
        <SpendControlCard />
      </div>

      {/* --- recent money rows --------------------------------------------------------- */}
      {seesMoney ? (
      <div className="bl-card bl-recent">
        <p className="bl-card-title bl-card-title-spaced">{portalText("finance", "m0199")}</p>
        {ledger.data === undefined || ledger.data.row.length === 0 ? (
          <p className="bl-card-note">{portalText("finance", "m0200")}</p>
        ) : (
          <div className="bl-scroll bl-money-table">
            <Table caption={portalText("finance", "m0201")} captionVisible={false}>
              <TableBody>
                {ledger.data.row.slice(0, 4).map((row) => (
                  <RecentRow key={row.ledgerEntryId} row={row} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      ) : null}

      {/* --- the organization's concurrency ------------------------------------------------ */}
      {/* Review #59: "Request quota" records a request the WeTalk team acts on. plan-014 SC-P: the export's card —
          "Concurrency for the organization", "<n> conversations at once · <plan>", and "Plans and add-ons" beside it
          (no add-on is sold at launch, so the note does not offer one). */}
      <div className="bl-tier">
        <span className="bl-tier-body">
          <span className="bl-tier-kicker">{portalText("finance", "m0202")}</span>
          <span className="bl-tier-name">
            {tier === undefined || organizationConcurrency === undefined
              ? portalText("finance", "m0203")
              : portalText("finance", "m0204", { value1: String(organizationConcurrency), value2: tier.name })}
          </span>
          <span className="bl-tier-note">{portalText("finance", "m0205")}</span>
          <PendingQuotaRequestLine />
        </span>
        <span className="bl-tier-actions">
          <Button
            variant="primary"
            onClick={() => {
              setQuotaRequestOpen(true);
            }}
          >{portalText("finance", "m0206")}</Button>
          <Link to="/finance/plans" className="btn btn-secondary">{portalText("finance", "m0207")}</Link>
        </span>
      </div>
      <QuotaRequestDialog
        open={quotaRequestOpen}
        currentCeiling={organizationConcurrency === undefined ? null : organizationConcurrency}
        onClose={() => {
          setQuotaRequestOpen(false);
        }}
      />

      {/* --- the minutes summary -------------------------------------------------------- */}
      <div className="bl-usage-head">
        <p className="bl-section-kicker">{portalText("finance", "m0208")}</p>
        <Segmented
          name="usageRange"
          legend={portalText("finance", "m1139")}
          value={usageRange}
          options={USAGE_RANGE.map((one) => ({ value: one, label: usageRangeLabel(one) }))}
          onChange={(next) => {
            setUsageRange(next);
          }}
        />
      </div>
      <MinutesSummary row={usage.data ?? []} />

      <div className="bl-lower">
        <div>
          <p className="bl-section-kicker bl-section-kicker-spaced">{portalText("finance", "m0209")}</p>
          {voiceAgentList.data === undefined || voiceAgentList.data.length === 0 ? (
            <p className="c-note">{portalText("finance", "m0210")}</p>
          ) : (
            <div className="bl-agent-table">
              <Table caption={portalText("finance", "m0211")} captionVisible={false}>
                <TableBody>
                  {voiceAgentList.data.map((one) => {
                    const mine = monthRow.filter((row) => row.voiceAgentId === one.voiceAgentId);
                    return (
                      <TableRow key={one.voiceAgentId}>
                        <TableCell>{one.displayName}</TableCell>
                        <TableCell numeric>
                          {talkTimeText(mine.reduce((total, row) => total + row.billedSeconds, 0))}
                        </TableCell>
                        <TableCell numeric>
                          {formatMoney(
                            mine.length === 0 ? noMoney() : sumMoney(mine.map((row) => row.charged)),
                          )}
                          {mine.some((row) => !row.closed) ? (
                            <span className="bl-soon" title={portalText("finance", "m0212")}>
                              {" "}{portalText("finance", "m0213")}</span>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {seesMoney ? (
        <div>
          <p className="bl-section-kicker bl-section-kicker-spaced">{portalText("finance", "m0214")}</p>
          {invoice.data === undefined || invoice.data.length === 0 ? (
            <p className="c-note">{portalText("finance", "m0215")}</p>
          ) : (
            <div className="bl-invoice-table">
              <Table caption={portalText("finance", "m0216")} captionVisible={false}>
                <TableBody>
                  {invoice.data.map((one) => (
                    <TableRow key={one.invoiceId}>
                      <TableCell>{monthLabel(new Date(`${one.periodMonth.slice(0, 10)}T12:00:00`))}</TableCell>
                      <TableCell numeric>{groupedCount(one.minuteTotal)}{portalText("finance", "m0218")}</TableCell>
                      <TableCell numeric>{formatMoney(one.amount)}</TableCell>
                      <TableCell numeric>
                        <span className="bl-soon" title={portalText("finance", "m0219")}>{portalText("finance", "m0220")}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        ) : null}
      </div>

      {funding !== undefined && managesMoney ? (
        <RefundDialog
          open={refundOpen}
          billingAccountId={funding.billingAccountId}
          onClose={() => {
            setRefundOpen(false);
          }}
        />
      ) : null}
      <AddCreditDialog
        open={addCreditOpen}
        onClose={() => {
          setAddCreditOpen(false);
        }}
      />
      <AddCardDialog
        open={addCardOpen}
        onClose={() => {
          setAddCardOpen(false);
        }}
      />
      {plan.canManage && plan.held !== null && plan.held !== undefined ? (
        <CardReplacementDialog open={replacingCard} held={plan.held} onClose={closeCardReplacement} />
      ) : null}
    </section>
  );
}

/**
 * Available's parts (2026-10-04): Balance (settled), talk time not yet settled today, held by
 * conversations in progress, and the free trial time where there is a grant — `creditPartsOf`'s lines,
 * which the operator console's account screen prints too, so both consoles show the same figures.
 */
export function CreditPartList({ balance }: { readonly balance: CreditBalanceView }): ReactNode {
  usePortalTranslation("finance");
  return (
    <dl className="bl-credit-part">
      {portalCreditPartsOf(creditFiguresOf(balance)).map((line) => (
        <div key={line.part} className="bl-credit-part-row">
          <dt>
            {line.label}
            {line.note === null ? null : <span className="bl-credit-part-note">{line.note}</span>}
          </dt>
          <dd>{line.text}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The tag beside the balance: the design's three words, plus the hard stop's own. */
function balanceStateLabel(balance: CreditBalanceView, low: boolean): string {
  if (balance.state === "hard_stopped") return portalText("finance", "m0221");
  if (balance.state === "exhausted") {
    return balance.refusal?.cause === "held_by_open_conversation" ? portalText("finance", "m0222") : portalText("finance", "m0223");
  }
  if (low) return portalText("finance", "m0161");
  return portalText("finance", "m0162");
}

/**
 * `About 3 hours of talk time left at <rate> a minute, your highest rate.` — or the free minutes first. Owner answer
 * OD-C: the estimate divides by the organization's HIGHEST rate in force and names it (`talkRatePerMinute` is the
 * API's `highest_talk_rate_minor_per_minute`), so it never promises more talk than the dearest kind allows.
 */
export function balanceLine(balance: CreditBalanceView): string {
  if (balance.refusal?.cause === "held_by_open_conversation") {
    return portalText("finance", "m0224");
  }
  if (balance.state === "exhausted" || balance.state === "hard_stopped") {
    return portalText("finance", "m0225");
  }
  const highest = balance.talkRatePerMinute;
  const minute = talkMinuteAffordableOf(balance.available, highest);
  if (minute === null) return portalText("finance", "billing.rates_free");
  const hours = Math.floor(minute / 60);
  const talk = hours >= 1 ? portalText("finance", "billing.talkHours", { count: hours }) : portalText("finance", "billing.talkMinutes", { count: minute });
  const rate = talkRateText(highest, "minute");
  if (balance.trialSecondsRemaining > 0) {
    return portalText("finance", "billing.trial_then_talk_at_highest", { value0: String(Math.round(balance.trialSecondsRemaining / 60)), value1: talk, value2: rate });
  }
  return portalText("finance", "billing.talk_left_at_highest", { value0: talk.charAt(0).toUpperCase() + talk.slice(1), value1: rate });
}

/**
 * "Invoices go to owner@romapizzeria.com · change" (design audit 2026-10-15): the billing profile's invoice emails, with
 * the way to the legal form (Finance → Documents, where they are edited). Read only by someone with the organization's
 * finance (the same read and cache key as the Finance kicker); nothing is drawn while there is no profile or no email.
 */
function InvoiceEmailLine(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway)).data;
  const scoped = context === undefined || context === null || context.kind === "selection_required" ? null : context;
  const readsFinance = scoped?.capability.account.includes("account.finance.read") === true;
  const profile = useQuery({
    queryKey: ["billing-profile", scoped?.accountId ?? "none"],
    queryFn: () => gateway.readBillingProfile(scoped?.accountId ?? ""),
    enabled: scoped !== null && readsFinance,
  });
  const email = profile.data === undefined || profile.data === null ? [] : profile.data.billingEmail;
  if (email.length === 0) return null;
  return (
    <p className="bl-card-foot">
      {portalText("finance", "invoices_go_to_value1_4e9b7344", { value1: email.join(", ") })}
      {" · "}
      <Link to="/finance/documents">{portalText("finance", "change_7550b672")}</Link>
    </p>
  );
}

function RecentRow({ row }: { readonly row: LedgerEntryView }): ReactNode {
  usePortalTranslation("finance");
  return (
    <TableRow>
      <TableCell>{row.whenText}</TableCell>
      <TableCell>
        <span className="bl-with-glyph">
          <Icon glyph={ledgerGlyphOf(row)} size="lg" tone="muted" />
          {ledgerLabelOf(row)}
        </span>
      </TableCell>
      <TableCell>{ledgerMethodOf(row)}</TableCell>
      <TableCell numeric>{formatSignedMoney(row.amount)}</TableCell>
      <TableCell numeric>
        <span className="bl-soon" title={portalText("finance", "m0230")}>{portalText("finance", "m0231")}</span>
      </TableCell>
    </TableRow>
  );
}


/**
 * One card, and its options behind the design's "Card options" button. Both acts change what
 * an auto-reload would charge, so both refresh the policy too.
 */
function PaymentMethodRow({ card, onReplaceCard }: { readonly card: PaymentMethodView; readonly onReplaceCard: (() => void) | null }): ReactNode {
  usePortalTranslation("finance");
  usePortalTranslation("subscription");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const { open, anchorRef, triggerRef, toggle, close } = usePopover();
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.paymentMethod });
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.autoReload });
  };
  const makeDefault = useMutation({
    mutationFn: () => gateway.setDefaultPaymentMethod(card.paymentMethodId),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => gateway.removePaymentMethod(card.paymentMethodId),
    onSuccess: refresh,
  });

  return (
    <div className="bl-method">
      <Icon glyph={GLYPH.creditCard} size="2xl" tone="muted" />
      <span className="bl-method-body">
        <span className="bl-method-line">
          <span className="bl-method-label">
            {cardBrandLabel(card.brand)} ···· {card.lastFour}
          </span>
          {card.isDefault ? <span className="bl-default">{portalText("finance", "m0232")}</span> : null}
        </span>
        <span className="bl-method-meta">{portalText("finance", "m0233")}{cardExpiryLabel(card.expiryMonth, card.expiryYear)} · {card.holderName}
        </span>
      </span>
      <div className="c-pop-anchor" ref={anchorRef}>
        <button
          ref={triggerRef}
          type="button"
          className="btn btn-icon"
          aria-label={portalText("finance", "m0234")}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => {
            toggle();
            globalThis.setTimeout(() => {
              anchorRef.current?.querySelector<HTMLButtonElement>("[role=\"menuitem\"]")?.focus();
            }, 0);
          }}
        >
          <Icon glyph={GLYPH.dotsThree} size="xl" />
        </button>
        {open ? (
          <div className="c-pop c-pop-right bl-method-menu" role="menu">
            <p className="c-pop-head">
              {card.hasMandate
                ? portalText("finance", "m0236")
                : portalText("finance", "m0237")}
            </p>
            {card.isDefault ? null : (
              <button
                type="button"
                role="menuitem"
                className="c-pop-item"
                onClick={() => {
                  close();
                  makeDefault.mutate();
                }}
              >{portalText("finance", "m0238")}</button>
            )}
            <button
              type="button"
              role="menuitem"
              className="c-pop-item"
              onClick={() => {
                close();
                remove.mutate();
              }}
            >{portalText("finance", "m0239")}</button>
          </div>
        ) : null}
      </div>
      {/* plan-020 WAPP-BILL (design §32.6.6, Q-28): the card a running plan charges is replaced, never removed. */}
      {remove.isError && refusalCodeOf(remove.error) === "payment_method_in_use_by_subscription" ? (
        <p className="bl-method-refusal" role="alert">
          {portalText("subscription", "refusal.payment_method_in_use_by_subscription")}
          {onReplaceCard === null ? null : (
            <>
              {" "}
              <button type="button" className="bl-link-button" onClick={onReplaceCard}>
                {portalText("billing", "plan.replace_card")}
              </button>
            </>
          )}
        </p>
      ) : remove.isError ? (
        <p className="bl-method-refusal" role="alert">
          {apiProblemText(remove.error, portalText("billing", "card.remove_failed"))}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Auto-reload.
 *
 * The threshold and the top-up are segmented choices rather than free text, because they are
 * amounts the platform charges without asking again and a mistyped one is a mistyped charge.
 * The monthly cap is what bounds the damage of a runaway, and it is shown rather than hidden.
 */
function AutoReloadCard(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const policy = useQuery(autoReloadQuery(gateway));
  const method = useQuery(paymentMethodQuery(gateway));

  const save = useMutation({
    mutationFn: (next: {
      enabled: boolean;
      thresholdMinor: number;
      topUpMinor: number;
      monthlyCapMinor: number;
      paymentMethodId: string;
    }) =>
      gateway.saveAutoReload({
        revision: policy.data?.revision ?? null,
        enabled: next.enabled,
        threshold: money(next.thresholdMinor, CURRENCY_CODE),
        topUp: money(next.topUpMinor, CURRENCY_CODE),
        monthlyCap: money(next.monthlyCapMinor, CURRENCY_CODE),
        paymentMethodId: next.paymentMethodId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.autoReload });
    },
  });

  if (policy.data === undefined) return <div className="bl-card">{portalText("finance", "m0240")}</div>;
  const current = policy.data;
  const card = method.data ?? [];
  const defaultCard = card.find((one) => one.isDefault) ?? card[0];
  const thresholdMinor = current.threshold?.amountMinor ?? THRESHOLD_MINOR[2];
  const topUpMinor = current.topUp?.amountMinor ?? TOP_UP_MINOR[1];
  const capMinor = current.monthlyCap?.amountMinor ?? topUpMinor * 10;

  const write = (
    patch: Partial<{ enabled: boolean; thresholdMinor: number; topUpMinor: number }>,
  ): void => {
    if (defaultCard === undefined) return;
    save.mutate({
      enabled: patch.enabled ?? current.enabled,
      thresholdMinor: patch.thresholdMinor ?? thresholdMinor,
      topUpMinor: patch.topUpMinor ?? topUpMinor,
      monthlyCapMinor: Math.max(capMinor, patch.topUpMinor ?? topUpMinor),
      paymentMethodId: current.paymentMethodId ?? defaultCard.paymentMethodId,
    });
  };
  const whole = (amountMinor: number): string =>
    formatWholeMoney(money(amountMinor, CURRENCY_CODE));

  return (
    <div className="bl-card">
      <div className="bl-auto-head">
        <span className="bl-auto-title">
          <span className="bl-card-kicker">{portalText("finance", "m0142")}</span>
          <span className="bl-auto-state">
            {current.enabled
              ? portalText("finance", "m0241", { value1: whole(topUpMinor), value2: whole(thresholdMinor) })
              : portalText("finance", "m0242")}
          </span>
        </span>
        <SwitchControl
          label={portalText("finance", "m0142")}
          labelVisible={false}
          checked={current.enabled}
          disabled={defaultCard === undefined}
          onChange={(enabled) => {
            write({ enabled });
          }}
        />
      </div>

      {defaultCard === undefined ? (
        <p className="bl-card-line bl-card-line-spaced">{portalText("finance", "m0243")}</p>
      ) : current.enabled ? (
        <>
          <div className="bl-auto-fields">
            <Field label={portalText("finance", "m0244")}>
              {() => (
                <span className="bl-seg-even">
                  <Segmented
                    name="autoReloadThreshold"
                    legend={portalText("finance", "m1140")}
                    value={String(thresholdMinor)}
                    options={THRESHOLD_MINOR.map((amount) => ({
                      value: String(amount),
                      label: whole(amount),
                    }))}
                    onChange={(next) => {
                      write({ thresholdMinor: Number(next) });
                    }}
                  />
                </span>
              )}
            </Field>
            <Field label={portalText("finance", "m0245")}>
              {() => (
                <span className="bl-seg-even">
                  <Segmented
                    name="autoReloadTopUp"
                    legend={portalText("finance", "m1141")}
                    value={String(topUpMinor)}
                    options={TOP_UP_MINOR.map((amount) => ({
                      value: String(amount),
                      label: whole(amount),
                    }))}
                    onChange={(next) => {
                      write({ topUpMinor: Number(next) });
                    }}
                  />
                </span>
              )}
            </Field>
          </div>
          <p className="bl-card-note bl-card-note-spaced">{portalText("finance", "m0246")}{cardBrandLabel(defaultCard.brand)} ···· {defaultCard.lastFour}{portalText("finance", "m0247")}{" "}
            {whole(capMinor)}{portalText("finance", "m0248")}</p>
        </>
      ) : (
        <p className="bl-card-line bl-card-line-spaced">{portalText("finance", "m0249")}</p>
      )}
    </div>
  );
}

/**
 * Spend controls, including the hard stop.
 *
 * **The switch records an instruction; it does not take a decision.** Whether an agent stops
 * answering is evaluated on every admission against design §7.9's live balance — the settled
 * balance less today's unpriced seconds less what conversations in flight may still cost.
 *
 * plan-014 GAP (INT decision 2026-10-07): the card writes through the workspace's SPEND CAP (E3a). A cap that allows an
 * overage before it stops is not a plain hard stop, so the switch cannot express it: it is shown, the switch is held,
 * and the overage is changed where the cap is — the workspace's page (and Finance → Allowances and caps). A budget or
 * warning change here keeps the cap's behaviour and overage as they are.
 */
function SpendControlCard(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const control = useQuery(spendControlQuery(gateway));
  const authSession = useQuery(authSessionQuery(gateway));
  // The workspace's name is the listing's, read on its own (customer-portal review #150).
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = authSession.data?.workspaceId ?? null;
  const [draftBudget, setDraftBudget] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (next: {
      monthBudgetMinor: number;
      alertThresholdPercent: number;
      hardStopEnabled: boolean;
    }) =>
      gateway.saveSpendControl({
        monthBudget: money(next.monthBudgetMinor, CURRENCY_CODE),
        alertThresholdPercent: next.alertThresholdPercent,
        hardStopEnabled: next.hardStopEnabled,
      }),
    onSuccess: async () => {
      setDraftBudget(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.spendControl });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.creditBalance });
    },
  });

  if (control.data === undefined) return <div className="bl-card">{portalText("finance", "m0250")}</div>;
  const current = control.data;
  // Review #57: no budget is no budget — the input is empty with a placeholder, never a prefilled
  // €2,500 that was never saved while "This month" says none is set. The alert and the hard stop
  // measure against a budget, so they wait for one.
  const budgetMinor = current.monthBudget?.amountMinor ?? null;
  const alertPercent = current.alertThresholdPercent ?? 75;
  const cappedOverage = current.capBehavior === "capped_overage";

  return (
    <div className="bl-card">
      <p className="bl-card-title bl-card-title-spaced">{portalText("finance", "m0251")}</p>
      <div className="bl-controls">
        <Field label={portalText("finance", "m0252", { value1: currentWorkspace.workspace?.name ?? portalText("finance", "m0187") })}>
          {(field) => (
            <span className="bl-budget">
              <Input
                {...field}
                inputMode="numeric"
                value={
                  draftBudget ??
                  (budgetMinor === null ? "" : formatWholeMoney(money(budgetMinor, CURRENCY_CODE)))
                }
                placeholder={portalText("finance", "m0253")}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setDraftBudget(next);
                }}
                onBlur={() => {
                  if (draftBudget === null) return;
                  const whole = Number(draftBudget.replaceAll(/[^0-9]/g, ""));
                  if (!Number.isFinite(whole) || whole <= 0) {
                    setDraftBudget(null);
                    return;
                  }
                  save.mutate({
                    monthBudgetMinor: Math.round(whole) * 100,
                    alertThresholdPercent: alertPercent,
                    hardStopEnabled: current.hardStopEnabled,
                  });
                }}
              />
            </span>
          )}
        </Field>
        <span className="bl-card-note bl-cap-note">{portalText("finance", "m0254")}{" "}
          <Link to="/finance/allowances">{portalText("finance", "m0255")}</Link>.
        </span>

        <Field label={portalText("finance", "m0256")}>
          {() =>
            budgetMinor === null ? (
              <span className="bl-card-note">{portalText("finance", "m0257")}</span>
            ) : (
              <span className="bl-seg-even">
                <Segmented
                  name="spendAlert"
                  legend={portalText("finance", "m1142")}
                  value={String(alertPercent)}
                  options={ALERT_PERCENT.map((percent) => ({
                    value: String(percent),
                    label: `${String(percent)}%`,
                  }))}
                  onChange={(next) => {
                    save.mutate({
                      monthBudgetMinor: budgetMinor,
                      alertThresholdPercent: Number(next),
                      hardStopEnabled: current.hardStopEnabled,
                    });
                  }}
                />
              </span>
            )
          }
        </Field>

        <span className="bl-hard-stop">
          <span className="bl-hard-stop-body">
            <span className="bl-hard-stop-title">{portalText("finance", "m0258")}</span>
            <span className="bl-hard-stop-dek">
              {budgetMinor === null
                ? portalText("finance", "m0259")
                : cappedOverage
                  ? portalText("finance", "m0260", { value1: current.overage === null ? portalText("finance", "m1261") : formatMoney(current.overage) })
                  : portalText("finance", "m0261")}
            </span>
            {cappedOverage && workspaceId !== null ? (
              <Link to="/workspaces/$workspaceId" params={{ workspaceId }} className="bl-hard-stop-link">{portalText("finance", "m0262")}</Link>
            ) : null}
          </span>
          <SwitchControl
            label={portalText("finance", "m0258")}
            labelVisible={false}
            checked={current.hardStopEnabled}
            disabled={budgetMinor === null || cappedOverage}
            onChange={(hardStopEnabled) => {
              if (budgetMinor === null || cappedOverage) return;
              save.mutate({
                monthBudgetMinor: budgetMinor,
                alertThresholdPercent: alertPercent,
                hardStopEnabled,
              });
            }}
          />
        </span>
      </div>
    </div>
  );
}

/**
 * Minutes a day.
 *
 * A bar per day, not a chart library: the value is one number per column and the height is a
 * percentage of a round ceiling above the tallest day, which is a custom property rather than a
 * length from the token scale. Today's bar is drawn in the accent, the rest in the neutral ramp,
 * as the design draws it; a day that was mostly test calls is drawn lighter still, because test
 * calls are billed and a summary that hid them would disagree with the invoice.
 */
function MinutesSummary({ row }: { readonly row: readonly UsageDayView[] }): ReactNode {
  usePortalTranslation("finance");
  if (row.length === 0) {
    return <p className="c-note">{portalText("finance", "m0263")}</p>;
  }
  const byDay = new Map<string, { billableSecond: number; isTestSecond: number }>();
  for (const one of row) {
    const held = byDay.get(one.usageDay) ?? { billableSecond: 0, isTestSecond: 0 };
    byDay.set(one.usageDay, {
      billableSecond: held.billableSecond + (one.isTest ? 0 : one.billedSeconds),
      isTestSecond: held.isTestSecond + (one.isTest ? one.billedSeconds : 0),
    });
  }
  const day = [...byDay.entries()].sort((left, right) => left[0].localeCompare(right[0]));
  // Heights are worked in seconds so a short day is not rounded to a whole minute's bar.
  const tallestSecond = Math.max(
    ...day.map(([, each]) => each.billableSecond + each.isTestSecond),
    SECOND_PER_MINUTE,
  );
  // A round ceiling a little above the tallest day (a whole hour), so the tallest bar is not always full.
  const ceilingSecond = Math.ceil((tallestSecond * 1.01) / SECOND_PER_HOUR) * SECOND_PER_HOUR;
  const first = day[0];
  const last = day[day.length - 1];

  return (
    <>
      <div className="bl-bars">
        {day.map(([dayKey, each], index) => {
          const talkSecond = each.billableSecond + each.isTestSecond;
          const open = row.some((one) => one.usageDay === dayKey && !one.closed);
          const tone =
            index === day.length - 1
              ? portalText("finance", "m0264")
              : each.isTestSecond > each.billableSecond
                ? portalText("finance", "m0265")
                : "bl-bar";
          return (
            <div
              key={dayKey}
              className={tone}
              title={`${shortDayLabel(dayKey)} · ${talkTimeText(talkSecond)}${open ? portalText("finance", "m0266") : ""}`}
              style={
                {
                  "--bl-bar-height": `${String(Math.round((talkSecond / ceilingSecond) * 100))}%`,
                } as CSSProperties
              }
            />
          );
        })}
      </div>
      <div className="bl-bars-axis">
        <span>{first === undefined ? "" : shortDayLabel(first[0])}</span>
        <span>{last === undefined ? "" : shortDayLabel(last[0])}</span>
      </div>
    </>
  );
}
